/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import {
  Component,
  JSXElement,
  onMount,
  onCleanup,
  createEffect,
} from 'solid-js';
import { computePosition, flip, shift, offset, arrow } from '@floating-ui/dom';
import {
  TOOLTIP_STYLES,
  CSS_CLASSES,
  ARROW_STYLES,
} from './custom-tooltip.const';
import { mergeRefs } from '../util';

const DEFAULT_DELAY_TIME = 0.7;
const DEFAULT_MAX_OPACITY = 0.9;

export type TooltipPositions =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'auto'
  | 'auto-start'
  | 'auto-end';

export interface CustomTooltipProps {
  /** Tooltip text content */
  text?: string;
  /** Tooltip position */
  position?: TooltipPositions;
  /** Disable dynamic overflow detection */
  notDynamic?: boolean;
  /** Disable tooltip entirely */
  disableToolTip?: boolean;
  /**
   * @deprecated Use `tooltipClass` instead.
   * Additional CSS classes
   */
  className?: string;
  /** Show tooltip arrow */
  arrow?: boolean;
  /** Content to wrap with tooltip */
  children?: JSXElement;
  /** Ref for web-component wrapper */
  containerRef?(el: HTMLDivElement): void;
  /** The tooltip class overrides. */
  tooltipClass?: string;
  /** The class overrides for custom tooltip element. */
  class?: string;
  /** The delay time before showing the tooltip. Default is 0.7 seconds. */
  delayTime?: number;
  /** The maximum opacity of the tooltip. Default is 0.9. */
  maxOpacity?: number;
}

const CustomTooltip: Component<CustomTooltipProps> = (props) => {
  let triggerElement: HTMLDivElement | undefined;
  let tooltipElement: HTMLDivElement | undefined;
  let arrowElement: HTMLDivElement | undefined;
  let cleanup: (() => void) | undefined;

  const setTriggerRef = (el: HTMLDivElement) => {
    triggerElement = el;
  };

  const getText = () => props.text || '';
  const getPosition = () => props.position || 'top';
  const getNotDynamic = () => props.notDynamic || false;
  const getDisableTooltip = () => props.disableToolTip || false;
  const getTooltipClass = () => props.className || props.tooltipClass || '';
  const getClass = () => props.class || '';
  const getArrow = () => props.arrow || false;

  // Check for text overflow (equivalent to React's isOverFlow method)
  const isOverFlow = (element: HTMLElement): boolean => {
    if (getNotDynamic()) {
      return true; // Always show if not dynamic
    }

    // Check if element itself has overflow
    const scrollWidth = element.scrollWidth;
    const offsetWidth = element.offsetWidth;
    if (scrollWidth > offsetWidth) {
      return true;
    }

    // Check child elements for overflow (like text content)
    const children = element.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      if (child.scrollWidth > child.offsetWidth) {
        return true;
      }
    }

    // If no overflow detected but we have text, show the tooltip anyway
    // This makes it work like most tooltip libraries
    return !!getText();
  };

  const updateArrowPosition = (
    placement: string,
    arrowData?: { x?: number; y?: number }
  ) => {
    if (getArrow() && arrowElement) {
      // Reset all arrow styles first
      Object.assign(arrowElement.style, ARROW_STYLES.base);

      // Apply position-specific arrow styles based on placement
      const side = placement.split('-')[0];
      if (side === 'top') {
        Object.assign(arrowElement.style, ARROW_STYLES.top);
        // Use floating-ui computed position if available
        if (arrowData?.x !== undefined) {
          // floating-ui provides the position relative to the tooltip
          // We need to account for the arrow's own width (6px) to center it properly
          arrowElement.style.left = `${arrowData.x.toString()}px`;
          arrowElement.style.transform = 'translateX(-50%)';
          arrowElement.style.marginLeft = '0'; // Remove the default margin when using precise positioning
        }
        // Default centering is already applied via ARROW_STYLES.top
      } else if (side === 'bottom') {
        Object.assign(arrowElement.style, ARROW_STYLES.bottom);
        // Use floating-ui computed position if available
        if (arrowData?.x !== undefined) {
          arrowElement.style.left = `${arrowData.x.toString()}px`;
          arrowElement.style.transform = 'translateX(-50%)';
          arrowElement.style.marginLeft = '0'; // Remove the default margin when using precise positioning
        }
        // Default centering is already applied via ARROW_STYLES.bottom
      } else if (side === 'left') {
        Object.assign(arrowElement.style, ARROW_STYLES.left);
        // Use floating-ui computed position if available
        if (arrowData?.y !== undefined) {
          arrowElement.style.top = `${arrowData.y.toString()}px`;
          arrowElement.style.transform = 'translateY(-50%)';
          arrowElement.style.marginTop = '0'; // Remove the default margin when using precise positioning
        }
        // Default centering is already applied via ARROW_STYLES.left
      } else if (side === 'right') {
        Object.assign(arrowElement.style, ARROW_STYLES.right);
        // Use floating-ui computed position if available
        if (arrowData?.y !== undefined) {
          arrowElement.style.top = `${arrowData.y.toString()}px`;
          arrowElement.style.transform = 'translateY(-50%)';
          arrowElement.style.marginTop = '0'; // Remove the default margin when using precise positioning
        }
        // Default centering is already applied via ARROW_STYLES.right
      }
    }
  };

  const updatePosition = async () => {
    if (!triggerElement || !tooltipElement) return;

    const middleware = [
      offset(getArrow() ? 12 : 8),
      flip(),
      shift({ padding: 8 }),
    ];

    // Add arrow middleware if arrow is enabled
    if (getArrow() && arrowElement) {
      middleware.push(arrow({ element: arrowElement }));
    }

    const { x, y, placement, middlewareData } = await computePosition(
      triggerElement,
      tooltipElement,
      {
        placement: getPosition() as any,
        middleware,
      }
    );

    Object.assign(tooltipElement.style, {
      left: `${x.toString()}px`,
      top: `${y.toString()}px`,
    });

    // Update the data-placement attribute with the actual computed placement
    tooltipElement.setAttribute('data-placement', placement);

    // Update arrow position and styles if arrow is enabled
    updateArrowPosition(placement, middlewareData.arrow);
  };

  const showTooltip = async () => {
    if (getDisableTooltip()) {
      return;
    }

    if (!tooltipElement) {
      return;
    }

    if (!triggerElement) {
      return;
    }

    const overflowCheck = isOverFlow(triggerElement);

    if (!overflowCheck) {
      return;
    }

    tooltipElement.style.transitionDelay = `${props.delayTime ?? DEFAULT_DELAY_TIME}s`;
    tooltipElement.style.visibility = 'visible';
    tooltipElement.style.opacity =
      props.maxOpacity?.toString() || DEFAULT_MAX_OPACITY.toString();
    await updatePosition();
  };

  const hideTooltip = () => {
    if (tooltipElement) {
      tooltipElement.style.transitionDelay = '0s';
      tooltipElement.style.opacity = '0';
      tooltipElement.style.visibility = 'hidden';
    }
  };

  const setDataPosition = (div: HTMLDivElement) => {
    const placement = getPosition();
    div.setAttribute('data-placement', placement);

    // Update arrow styles when position changes
    // Note: We don't have arrow positioning data in this context, so use centering
    updateArrowPosition(placement);
  };

  onMount(() => {
    if (!triggerElement) {
      return;
    }

    if (!getText()) {
      return;
    }

    // Create tooltip element
    tooltipElement = document.createElement('div');
    tooltipElement.className = `${CSS_CLASSES.tooltipBox} ${CSS_CLASSES.trimbleTooltip} ${getTooltipClass()}`;
    tooltipElement.setAttribute('role', 'tooltip');
    setDataPosition(tooltipElement);
    Object.assign(tooltipElement.style, TOOLTIP_STYLES);

    // Create content element
    const contentElement = document.createElement('div');
    contentElement.className = CSS_CLASSES.tooltipContent;
    contentElement.setAttribute('data-state', 'visible');
    contentElement.textContent = getText();
    tooltipElement.appendChild(contentElement);

    // Create arrow element if arrow prop is true
    if (getArrow()) {
      arrowElement = document.createElement('div');
      arrowElement.className = CSS_CLASSES.tooltipArrow;
      Object.assign(arrowElement.style, ARROW_STYLES.base);
      tooltipElement.appendChild(arrowElement);
    }

    document.body.appendChild(tooltipElement);

    // Set up event listeners
    const handleMouseEnter = () => void showTooltip();
    const handleMouseLeave = () => {
      hideTooltip();
    };
    const handleFocus = () => void showTooltip();
    const handleBlur = () => {
      hideTooltip();
    };

    triggerElement.addEventListener('mouseenter', handleMouseEnter);
    triggerElement.addEventListener('mouseleave', handleMouseLeave);
    triggerElement.addEventListener('focusin', handleFocus);
    triggerElement.addEventListener('focusout', handleBlur);
    triggerElement.addEventListener('click', handleMouseLeave);

    // eslint-disable-next-line solid/reactivity
    cleanup = () => {
      if (triggerElement) {
        triggerElement.removeEventListener('mouseenter', handleMouseEnter);
        triggerElement.removeEventListener('mouseleave', handleMouseLeave);
        triggerElement.removeEventListener('focusin', handleFocus);
        triggerElement.removeEventListener('focusout', handleBlur);
        triggerElement.removeEventListener('click', handleMouseLeave);
      }

      if (tooltipElement && tooltipElement.parentNode) {
        tooltipElement.parentNode.removeChild(tooltipElement);
      }
    };
  });

  onCleanup(() => {
    if (cleanup) cleanup();
  });

  // Update tooltip content when text changes
  createEffect(() => {
    if (tooltipElement && getText()) {
      const contentElement = tooltipElement.querySelector(
        `.${CSS_CLASSES.tooltipContent}`
      );
      if (contentElement) {
        contentElement.textContent = getText();
      }
      setDataPosition(tooltipElement);
    }
  });

  // Update position when position prop changes
  createEffect(() => {
    getPosition(); // Track position changes
    if (tooltipElement) {
      setDataPosition(tooltipElement);
    }
  });

  return (
    <div
      ref={mergeRefs<HTMLDivElement>((el) => {
        setTriggerRef(el);
      }, props.containerRef)}
      // eslint-disable-next-line @typescript-eslint/naming-convention
      style={{ display: 'inline-block', 'vertical-align': 'middle' }}
      class={getClass()}
    >
      {props.children}
    </div>
  );
};

export default CustomTooltip;
